import {
  EyeOutlined,
  QuestionCircleOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import { AddPhotoIcon } from '@assets/icons/addPhoto';
import {
  Image as AntImage,
  message,
  Progress,
  Row,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/lib/upload';
import { DragEvent, FC, useState } from 'react';
import { ControllerProps, get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { env } from 'src/app/env';
import { useUploadProgramsFile } from 'src/features/programsV2/hooks/useUploadProgramFile';
import { VisualStepForm } from 'src/features/programsV2/schemas/visualStepSchema';
import { showErrorModal } from 'src/features/programsV2/utils/modalHelpers';
import { isFileSizeLess } from 'src/features/programsV2/utils/validateFileSize';

import { TransparentButton } from '../../ui/TransparentButton';

interface ImageUploadProps {
  title: string;
  helperText: string;
  fieldName: ControllerProps<VisualStepForm>['name'];
  maxDimensions: {
    width: number;
    height: number;
  };
  templateHref?: string;
  disabled?: boolean;
}

const { Text } = Typography;

export const ImageUpload: FC<ImageUploadProps> = ({
  title,
  fieldName,
  helperText,
  maxDimensions,
  disabled,
}) => {
  const {
    setValue,
    formState: { errors },
    clearErrors,
    watch,
  } = useFormContext<VisualStepForm>();

  const params = useParams<{ id: string }>();
  const programId = Number(params.id);

  const defaultImage = watch(`${fieldName}.preview` as any);
  const hasSelectedLocalFile = watch(`${fieldName}.file` as any);

  const previewImage =
    (hasSelectedLocalFile && defaultImage) ||
    (defaultImage && `${env.PROGRAM_ASSETS}/${defaultImage}`);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { uploadFile, progress, isUploading } = useUploadProgramsFile();

  const handlePreview = (event: any) => {
    event.stopPropagation();
    setIsPreviewOpen(true);
  };

  const handleSetLocalPreviewImage = (
    imageUrl: string,
    file: RcFile | File
  ) => {
    setValue(
      `${fieldName}` as any,
      { preview: imageUrl, file },
      { shouldValidate: true }
    );
  };

  const uploadImage = async (file: RcFile | File) => {
    const tempUrlImage = URL.createObjectURL(file);

    const formData = new FormData();
    formData.append(fieldName, file);

    try {
      await uploadFile({ file: formData, programId });
      handleSetLocalPreviewImage(tempUrlImage, file);
      clearErrors(fieldName);
    } catch (error) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content: 'Não foi possível salvar este arquivo',
      });
    }
  };

  const handleValidateFileType = (file: RcFile | File) => {
    const fileType = file.type;

    if (fileType !== 'image/png') {
      message.error(
        `${file.name.toLowerCase()} - não é um arquivo válido. Apenas arquivos PNG são permitidos.`
      );
      return false;
    }
    return true;
  };

  const validateImageDimensions = (file: RcFile | File): Promise<boolean> => {
    return new Promise(resolve => {
      const image = new Image();
      image.src = URL.createObjectURL(file);

      image.onload = () => {
        const { width, height } = image;
        URL.revokeObjectURL(image.src);

        if (width < maxDimensions?.width || height < maxDimensions?.height) {
          message.error(
            `A imagem deve ter ${maxDimensions?.width}x${maxDimensions?.height} pixels. ` +
              `Dimensões da imagem selecionada: ${width}x${height} pixels.`
          );
          resolve(false);
        }
        resolve(true);
      };
    });
  };

  const handleChange = async (info: UploadChangeParam<UploadFile>) => {
    const file = info.file as RcFile;

    if (!handleValidateFileType(file)) {
      return;
    }

    const isValidDimension = await validateImageDimensions(file);
    if (!isValidDimension) {
      return;
    }

    const isValidSize = isFileSizeLess(file, 10);
    if (!isValidSize) {
      return;
    }

    await uploadImage(file);
  };

  const handleChangeImage = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/png';

    fileInput.addEventListener('change', async e => {
      const target = e.target as HTMLInputElement;
      if (!target.files || target.files.length === 0) {
        return;
      }
      const file = target.files[0];

      if (!handleValidateFileType(file)) {
        return;
      }

      const isValidDimension = await validateImageDimensions(file);
      if (!isValidDimension) {
        return;
      }

      if (file) {
        await uploadImage(file);
      }
    });

    fileInput.click();
  };

  const error = get(errors, `${fieldName}.preview`);

  const handleDropValidation = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;

    if (droppedFiles.length) {
      const file = droppedFiles[0] as RcFile;
      handleValidateFileType(file);
    }
  };

  return (
    <div>
      <Row align='middle' style={{ marginBottom: '8px' }}>
        <Text strong>{title}</Text>

        <Tooltip title={helperText}>
          <QuestionCircleOutlined style={{ marginLeft: 4 }} />
        </Tooltip>
      </Row>
      <StyledUpload
        beforeUpload={() => false}
        onChange={handleChange}
        showUploadList={false}
        accept='image/png'
        onDrop={handleDropValidation}
        disabled={disabled}
        isError={!!error}
      >
        {!!previewImage || isUploading ? (
          <>
            {isUploading ? (
              <DefaultPhotoContainer>
                <Progress
                  percent={progress}
                  status={'active'}
                  size='small'
                  style={{
                    padding: 32,
                    justifySelf: 'center',
                    width: '100%',
                  }}
                />
              </DefaultPhotoContainer>
            ) : (
              <AntImage
                src={previewImage}
                alt='Preview'
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                }}
                width='auto'
                height='200px'
                style={{
                  objectFit: 'cover',
                  pointerEvents: disabled ? 'none' : 'unset',
                }}
                preview={{
                  visible: isPreviewOpen,
                  onVisibleChange: visible => setIsPreviewOpen(visible),
                  mask: (
                    <Row>
                      <TransparentButton
                        icon={<EyeOutlined />}
                        onClick={handlePreview}
                      />
                      <TransparentButton
                        icon={<RetweetOutlined />}
                        onClick={handleChangeImage}
                      />
                    </Row>
                  ),
                }}
              />
            )}
          </>
        ) : (
          <DefaultPhotoContainer>
            <AddPhotoIcon />
            <p style={{ fontSize: 16 }}>
              <strong style={{ color: '#1668DC' }}>
                Clique para aqui carregar
              </strong>{' '}
              ou arraste e solte
            </p>
            <Text type='secondary'>{helperText}</Text>
          </DefaultPhotoContainer>
        )}
      </StyledUpload>
      {!!error && (
        <Text type='danger' style={{ marginTop: '8px', display: 'flex' }}>
          {error.message}
        </Text>
      )}
    </div>
  );
};

const StyledUpload = styled(Upload.Dragger)<{ isError: boolean }>`
  .ant-upload.ant-upload-drag {
    border: ${({ isError }) =>
      isError ? '1px dashed red !important' : undefined};
  }

  &.ant-upload.ant-upload-drag .ant-upload {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    padding: 0;
    min-height: 176px;
  }
  .ant-upload-drag-container {
    width: 100%;
  }

  &.ant-upload-disabled {
    background: #2c2c2c;
  }
`;

const DefaultPhotoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  height: 200px;
`;
